/* eslint-disable no-param-reassign */

import _ from 'es-toolkit/compat';
import * as yup from 'yup';

import { CORPORATE_STRUCTURE, INDUSTRIES } from '@clearverify/constants';
import { validateSyncWithSchema } from '@clearverify/utils';

import {
  emailValidationSchema,
  feinValidationSchema,
  numberOfEmployeesSimpleValidationSchema,
  phoneNumberValidationSchema,
  taxYearEndDateValidationSchema,
  zipCodeValidationSchema,
} from 'src/constants/validation';
import { collectPropertyValuesCount } from 'src/utils/bulkUpload';

import { TemplateField } from '../constants';
import type { ExtendedInvitedPeoData, InvitedPeoData } from '../types';

const NOT_UNIQUE_MESSAGE = ' - Not unique value';
const NOT_VALID_MESSAGE = ' - Invalid value';
const REQUIRED_MESSAGE = 'Required value';

export const validatePeos = (peos: InvitedPeoData[]): ExtendedInvitedPeoData[] => {
  const inviteEmails = collectPropertyValuesCount(peos, TemplateField.emailForInvitation, value =>
    _.isString(value) ? value.trim() : value,
  );

  const resultData: ExtendedInvitedPeoData[] = [];
  for (const peo of peos) {
    const isEmailForInvitationUnique = peo.emailForInvitation
      ? (inviteEmails[(peo.emailForInvitation || '_').trim()] || 1) === 1
      : true;

    let valid = isEmailForInvitationUnique;

    if (!isEmailForInvitationUnique) peo.emailForInvitation += NOT_UNIQUE_MESSAGE;

    const validationErrors = validateSyncWithSchema(invitedPeoValidationSchema, peo);
    if (!_.isEmpty(validationErrors)) valid = false;

    for (const property in validationErrors) {
      if (
        [TemplateField.corporateStructure, TemplateField.industry].includes(
          property as TemplateField,
        )
      ) {
        const peoProp = _.get(peo, property);
        _.set(peo, property, peoProp + NOT_VALID_MESSAGE);
      } else {
        // eslint-disable-next-line no-lonely-if
        if (property in peo) {
          const peoProp = _.get(peo, property);
          _.set(peo, property, `${peoProp} - ${validationErrors[property]}`);
        } else {
          _.set(peo, property, validationErrors[property]);
        }
      }
    }

    const result: ExtendedInvitedPeoData = {
      ...peo,
      id: window.crypto.randomUUID(),
      valid,
    };

    resultData.push(result);
  }

  return resultData;
};

const stringValidationSchema = yup.string().trim();

const invitedPeoValidationSchema: yup.ObjectSchema<InvitedPeoData> = yup.object({
  [TemplateField.fein]: feinValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.contactEmail]: emailValidationSchema.trim().required(REQUIRED_MESSAGE),
  [TemplateField.contactPhone]: phoneNumberValidationSchema.nullable(),
  [TemplateField.corporateStructure]: stringValidationSchema.oneOf(CORPORATE_STRUCTURE).nullable(),
  [TemplateField.displayName]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.emailForInvitation]: emailValidationSchema.trim().nullable(),
  [TemplateField.hqAddress]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqCity]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqCounty]: stringValidationSchema.nullable(),
  [TemplateField.hqState]: stringValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.hqZipCode]: zipCodeValidationSchema.required(REQUIRED_MESSAGE),
  [TemplateField.industry]: stringValidationSchema.oneOf(INDUSTRIES).nullable(),
  [TemplateField.numberOfEmployees]: numberOfEmployeesSimpleValidationSchema,
  [TemplateField.taxYearEndDate]: taxYearEndDateValidationSchema.nullable(),
});
